/**
 * signal-stance.js — WHAT THE TAPE THINKS OF A POSITION HE ALREADY HOLDS.
 *
 * Trades part 2b. His choice on 2026-08-16, after the measurement in
 * `reports/SIGNAL-TRADE-STANCE-2026-08-16.md` rewrote the spec: the narrow version — *"does the
 * latest call oppose your side"* — says almost nothing on his book, and what it does say is
 * misleading. So the stance carries **three** things, not one.
 *
 * ── WHY THE NARROW VERSION HAD TO BE WIDENED ────────────────────────────────────────────────
 *
 * Measured on the live book (`zagx-2026-08-14.json`): 8 real open trades, and **2 of them have
 * any signal at all**. "Latest call vs your side" produces one green tick (ASELS), one shrug
 * (PGSUS, three WAITs) and six blanks.
 *
 * ⚠ **AND THE GREEN TICK IS THE MISLEADING ONE.** The same analyst said **SELL ASELS at 355 on
 * 30 July** and **BUY at 411 on 13 August**. The 30 July call is still `TRIGGERED` — unresolved,
 * seventeen days later, live on the Risk page. A badge reading *"the latest call agrees"* next to
 * an open opposing call from the same source **launders a flip-flop into an endorsement**. So an
 * unresolved opposing call outranks the latest one, and the verdict is CONFLICTED.
 *
 * ── EVERY TRADE IN HIS BOOK IS LONG ─────────────────────────────────────────────────────────
 *
 * All 18, open and closed, are `side: "Long"`. **Nothing in his data exercises a Short**, so the
 * mirrored half is proved by tests only. Said here rather than discovered later (rule #45).
 * An unrecognised side answers `null` from `sideOf` and gets its own verdict, `'unreadable'` —
 * never AGREES, never CONFLICTED. Claiming a contradiction against a side you do not understand,
 * or worse endorsing one, is the worst of the available outcomes. ⚠ This paragraph was written
 * before the branch existed and was **false for the first hour**: `opposes` returns false for a
 * null side, so a garbage side fell straight through to `'agrees'`. The first live run caught it.
 * Rule #41 — a doctrine written next to code that breaks it is decoration.
 *
 * ── NO STATE, NO MIGRATION, NO NEW FIELD ────────────────────────────────────────────────────
 *
 * Derived at render from rows that already exist, exactly like `thesisVerdict`. Nothing is
 * stamped and no background job runs. If the tape changes, the answer changes.
 *
 * ⚠ **THIS FILE IS PURE AND IMPORTS NOTHING**, same as `thesis.js`. The join to `S` lives in
 * `signals.js`, which already owns the position↔signal matching (`_sigPositionTickers`,
 * `normTicker`) — part 2b **extends** that module rather than growing a second matcher.
 *
 * (2026-08-16)
 */

/**
 * Action → direction, written against the **measured seven**, not from memory.
 *
 * ⚠ `SELECT DISTINCT action` on the live book returns exactly:
 *   `HOLD · WAIT · BUY · SELL · BUY_ON_CONDITION · BUY_ON_DIP · BUY_WITH_STOP`
 *
 * A naive `action === 'BUY'` therefore misses **three bullish variants**. And the vocabulary in
 * anyone's head is not the vocabulary the ingest emits: `signals.js:_actionBadge` colours a
 * `WATCH` that has **never once been written**, which is why four of the seven render grey on the
 * live Signals page today. Rule #48.
 */
export const ACTION_DIRECTION = {
  BUY: 'bull',
  BUY_ON_CONDITION: 'bull',
  BUY_ON_DIP: 'bull',
  BUY_WITH_STOP: 'bull',
  SELL: 'bear',
  HOLD: 'flat',
  WAIT: 'flat',
};

/**
 * A call is RESOLVED once he has acted on it or it has lapsed. Everything else is still standing.
 *
 * ⚠ Deliberately a list of what IS resolved, not of what is not. `APPROACHING` never occurs in
 * his data and `NEW` is the commonest status; an allowlist of "still open" statuses would drop a
 * status the ingest starts emitting tomorrow into silence, which is the failure mode that matters
 * here — a live opposing call that stops being reported.
 */
export const RESOLVED_STATUSES = ['ACTED', 'EXPIRED'];

const _s = v => (typeof v === 'string' ? v.trim() : '');

/** `'bull' | 'bear' | 'flat'`, or `null` for an action this file has never been told about. */
export function actionDirection(action) {
  const k = _s(action).toUpperCase();
  return ACTION_DIRECTION[k] || null;
}

/** `'long' | 'short'`, or `null`. An unrecognised side is NOT assumed to be long. */
export function sideOf(trade) {
  const s = _s(trade && trade.side).toLowerCase();
  if (s === 'long') return 'long';
  if (s === 'short') return 'short';
  return null;
}

/** Does a directional call point against this side? Unknown either way ⇒ false. */
export function opposes(direction, side) {
  if (!side) return false;
  if (side === 'long') return direction === 'bear';
  if (side === 'short') return direction === 'bull';
  return false;
}

/** Has this call been answered — acted on, or lapsed? */
export function isResolved(sig) {
  return RESOLVED_STATUSES.includes(_s(sig && sig.status).toUpperCase());
}

/**
 * The day a report is ABOUT, falling back to the day it was ingested.
 *
 * `sourceDate` is the analyst's own date and is what the whole app sorts signals by; `createdAt`
 * is when the pull happened, which can be days later. The `createdAt` tail only breaks ties so
 * that "latest" is deterministic for two reports published the same day.
 */
export function signalOrderKey(sig) {
  const d = _s(sig && sig.sourceDate) || _s(sig && sig.createdAt).slice(0, 10);
  return d + ' ' + _s(sig && sig.createdAt);
}

/**
 * THE STANCE. Returns `null` when the tape has never mentioned this ticker.
 *
 * ⚠ **NULL MEANS RENDER NOTHING — not a grey "no signals" chip.** Six of his eight open trades
 * are in this state, and a placeholder on every one of them would be an absence rendered as a
 * measurement, the same failure as back-filling a default (rule #28).
 *
 * @param {Object} trade      the open trade
 * @param {Array}  signals    the signals ALREADY MATCHED to this trade's ticker by the caller
 * @returns {Object|null} {
 *   verdict:  'conflicted' | 'superseded' | 'agrees' | 'nocall' | 'unreadable',
 *   count:    distinct report lines (not rows — see below),
 *   sources:  distinct source names — see the note on the field,
 *   latest:   the most recent call of any kind,
 *   latestDir:the most recent DIRECTIONAL call, or null,
 *   opposing: unresolved directional calls against the side, newest first,
 *   supersededBy: later NON-directional calls from the same source, newest first,
 *   first/last: the window the tape covers,
 *   unknown:  calls whose action this file does not recognise
 * }
 */
export function signalStance(trade, signals) {
  const rows = (signals || []).filter(Boolean);
  if (!rows.length) return null;

  const side = sideOf(trade);

  // ⚠ ONE REPORT LINE, ONE ENTRY — INCLUDING IN `opposing`, NOT JUST IN THE COUNT. `_extId` is the
  // importer's own key for one analyst's call on one asset in one report. `25e43c2` collapses the
  // duplicates at save, but a pull or a merge that has not been saved yet still has both in
  // memory, and the first live run of this file printed the **same warning line twice** for the
  // 13 Aug ASELS call. "2 calls say BUY" meaning one analyst counted twice is the exact lie this
  // panel must not tell.
  //
  // This is NOT a second copy of the survivor rule in `state.js`: it deletes nothing, and it never
  // has to choose between differing content, because rows sharing an `_extId` are the same report
  // line by construction. Falls back to `id`, then to position, so a hand-pasted signal with
  // neither is still kept exactly once.
  // Sorted in place: `rows` is the `.filter(Boolean)` result above and is never the caller's
  // array. A defensive `.slice()` was written here first and the mutation run proved it
  // unobservable — the second time today. Unkillable code is a habit, not a guard (rule #50).
  const sortedAll = rows.sort((a, b) => signalOrderKey(b).localeCompare(signalOrderKey(a)));
  const seen = new Set();
  const sorted = sortedAll.filter((s, i) => {
    const k = _s(s._extId) || _s(s.id) || 'row:' + i;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const count = sorted.length;

  const graded = sorted.map(s => ({ sig: s, dir: actionDirection(s.action) }));
  const unknown = graded.filter(g => g.dir === null).length;
  const directional = graded.filter(g => g.dir === 'bull' || g.dir === 'bear');

  const latest = sorted[0];
  const latestDir = directional.length ? directional[0] : null;
  const opposing = directional
    .filter(g => opposes(g.dir, side) && !isResolved(g.sig))
    .map(g => g.sig);

  // ⚠ A DIRECTIONAL CALL THE SAME SOURCE HAS SINCE STOOD DOWN FROM IS NOT THE CURRENT STANCE.
  // `latestDir` walks back past any number of neutral calls with no limit, so a BUY from three
  // weeks ago outranked three WAITs published on top of it — and the panel said AGREES.
  //
  // That is the SAME laundering the CONFLICTED branch exists to prevent, arriving by staleness
  // instead of by an opposing call, and I shipped it in `2337cd9`. Measured 2026-08-16 while
  // scoping the holdings surface: **four of his eight holdings** read AGREES on a call their own
  // source had already moved off — BTC (BUY 6 Aug, then WAIT on the 8th, 12th and 13th), ETH and
  // XRP the same, THYAO (BUY 5 Aug, HOLD on the 7th). **All 9 of the 9 superseding calls are from
  // the same source**, so the rule needs no fuzziness: it is supersession by the author, not
  // disagreement between authors, and a WAIT from a different analyst stands down nobody.
  // ⚠ FLAT ONLY, NOT "anything later". An action this file cannot read is not KNOWN to be a stand
  // down — `STRONG_SELL` from the same source the next day is more likely the opposite — and it
  // already has its own channel in `unknown` and its own line on the card. Extending supersession
  // to unrecognised actions would be inventing past the measurement, which was nine WAITs and
  // HOLDs. An existing test caught the overreach on the first run.
  const supersededBy = latestDir
    ? graded.slice(0, graded.indexOf(latestDir))
      .filter(g => g.dir === 'flat'
        && _s(g.sig.sourceName) && _s(g.sig.sourceName) === _s(latestDir.sig.sourceName))
      .map(g => g.sig)
    : [];

  // ORDER IS LOAD-BEARING, and it is the whole point of part 2b. An unresolved opposing call wins
  // over a friendly latest one — see the docblock. A latest directional call that opposes also
  // reads CONFLICTED even when it is resolved, because "the last thing this source said was
  // against you" is a contradiction whatever its status.
  //
  // ⚠ AND AN UNREADABLE SIDE CLAIMS NOTHING. `opposes` answers false for a null side, so without
  // this first branch a garbage `side` fell straight through to **'agrees'** — the docblock above
  // promised it could never do that, and the first live run proved the docblock was decoration
  // (rule #41). The tape is still returned; the verdict simply refuses to read it.
  //
  // ⚠ SUPERSESSION SITS BELOW BOTH CONFLICT BRANCHES AND ABOVE AGREES. An open opposing call is
  // still the more urgent fact; but a friendly call its own author has stood down from must never
  // reach AGREES.
  let verdict;
  if (!side) verdict = 'unreadable';
  else if (opposing.length) verdict = 'conflicted';
  else if (latestDir && opposes(latestDir.dir, side)) verdict = 'conflicted';
  else if (latestDir && supersededBy.length) verdict = 'superseded';
  else if (latestDir) verdict = 'agrees';
  else verdict = 'nocall';

  const dates = sorted.map(s => _s(s.sourceDate) || _s(s.createdAt).slice(0, 10)).filter(Boolean).sort();

  return {
    verdict,
    count,
    // ⚠ THIS WAS `sourceSpellings` FOR ONE COMMIT, AND THE CARD REFUSED TO RENDER IT. One
    // analyst's channel name existed in the book under two strings — a UTF-8 name decoded as GBK
    // upstream — so this counted 2 for both of his tickers where the truthful answer was 1, and a
    // number known to be wrong is worse than no number. `utils.demojibake`, applied at `save()`,
    // makes the two spellings one; measured on his book, 27 strings repaired and both tickers now
    // answer 1. The name went back to what it means.
    sources: new Set(sorted.map(s => _s(s.sourceName)).filter(Boolean)).size,
    latest,
    latestDir: latestDir ? latestDir.sig : null,
    opposing,
    supersededBy,
    first: dates[0] || null,
    last: dates[dates.length - 1] || null,
    unknown,
    side,
  };
}
